import React, { useEffect, useMemo, useRef, useState } from "react";
import { AdminHeader } from "../Common/CompanyAdminHeader";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import OrderManagement from "../../pages/CompanyAdmin/OrderManagement/index";
import ResubmissionManagement from "../../pages/CompanyAdmin/ResubmissionManagement";
import ShippingManagement from "../../pages/CompanyAdmin/ShippingManagement";
import type { CompanyInfoResponse } from "domain/types/shared";
import { companyUseCase } from "di/container";
import { LucidePlus } from "lucide-react";

interface CompanyAdminLayoutProps {
  children: React.ReactNode;
}

const PAGES = {
  HOME: "HOME",
  COMPANY_MANAGEMENT: {
    INFO: "INFO",
    MALL_DESIGN: "MALL_DESIGN",
    ORGANIZATION: "ORGANIZATION",
    EQUIPMENTS: "EQUIPMENTS",
    EMAIL_SETTINGS: "EMAIL_SETTINGS",
    ROUTERS: "ROUTERS",
  },
  CUSTOMER_MANAGEMENT: {
    MEMBERS: "MEMBERS",
    BUSINESS_CLIENTS: "BUSINESS_CLIENTS",
    QUOTE_INQUIRIES: "QUOTE_INQUIRIES",
    ORDER_REVISION_REQUESTS: "ORDER_REVISION_REQUESTS",
  },
  PAYMENT_MANAGEMENT: {
    BANK_TRANSFER: "BANK_TRANSFER",
    TOSS_PAYMENTS: "TOSS_PAYMENTS",
  },
  ORDER_MANAGEMENT: {
    ALL_ORDERS: "ALL_ORDERS",
    RESUBMISSION: "RESUBMISSION",

    SHIPPING: "SHIPPING",
    TAX_RECEIPT: "TAX_RECEIPT",
    REFUND: "REFUND",
    NOTIFICATIONS: "NOTIFICATIONS",
    STATUS_MANAGEMENT: "STATUS_MANAGEMENT",
    MAIL_INBOX: "MAIL_INBOX", // 주문 메일함
    MAIL_SYNC_SETTING: "MAIL_SYNC_SETTING", // 메일 연동 설정
  },
  PRODUCT_MANAGEMENT: {
    PRODUCTS: "PRODUCTS",
    OPTIONS: "OPTIONS",
  },
  MATERIAL_MANAGEMENT: {
    MATERIAL: "MATERIAL",
    INVENTORY: "INVENTORY",
  },
  TASK_MANAGEMENT: {
    TASK_TEMPLATES: "TASK_TEMPLATES",
    STATUS: "STATUS",
    PRODUCTION_RECORDS: "PRODUCTION_RECORDS",
    MAINTENANCE: "MAINTENANCE",
  },
  CONTENT_MANAGEMENT: {
    NOTICES: "NOTICES",
    FAQS: "FAQS",
    FONTS: "FONTS",
    POPUPS: "POPUPS",
    PORTFOLIOS: "PORTFOLIOS",
  },
  STATISTICS: {
    KPI: "KPI",
  },
  PRICING_MANAGEMENT: {
    WASTE_RATE: "WASTE_RATE",
    SHIPPING_FEE: "SHIPPING_FEE",
    EVENT_DISCOUNT: "EVENT_DISCOUNT",
  },
} as const;
type PageCategories = Record<
  Exclude<keyof typeof PAGES, "HOME">,
  Exclude<keyof typeof PAGES, "HOME">
>;
const PAGE_CATEGORIES = Object.fromEntries(
  Object.keys(PAGES)
    .filter((key) => key !== "HOME")
    .map((key) => [key, key]),
) as PageCategories;
type DeepValue<T> = T extends string
  ? T
  : T extends object
    ? DeepValue<T[keyof T]>
    : never;
type SelectedPage = DeepValue<typeof PAGES>;

class PageManager {
  constructor(
    private pathname: string,
    private pages: typeof PAGES,
    private companyId?: number | null,
  ) {}

  getAutoExpandingMenu(): string | null {
    const selectedPage = this.checkLocation();

    if (
      selectedPage !== null &&
      (selectedPage === this.pages.COMPANY_MANAGEMENT.INFO ||
        selectedPage === this.pages.COMPANY_MANAGEMENT.MALL_DESIGN ||
        selectedPage === this.pages.COMPANY_MANAGEMENT.ORGANIZATION ||
        selectedPage === this.pages.COMPANY_MANAGEMENT.EQUIPMENTS ||
        selectedPage === this.pages.COMPANY_MANAGEMENT.EMAIL_SETTINGS ||
        selectedPage === this.pages.COMPANY_MANAGEMENT.ROUTERS)
    ) {
      return "COMPANY_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.CUSTOMER_MANAGEMENT.MEMBERS ||
        selectedPage === this.pages.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS ||
        selectedPage === this.pages.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES ||
        selectedPage === this.pages.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS)
    ) {
      return "CUSTOMER_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.PAYMENT_MANAGEMENT.BANK_TRANSFER ||
        selectedPage === this.pages.PAYMENT_MANAGEMENT.TOSS_PAYMENTS)
    ) {
      return "PAYMENT_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.ORDER_MANAGEMENT.ALL_ORDERS ||
        selectedPage === this.pages.ORDER_MANAGEMENT.RESUBMISSION ||
        selectedPage === this.pages.ORDER_MANAGEMENT.SHIPPING ||
        selectedPage === this.pages.ORDER_MANAGEMENT.TAX_RECEIPT ||
        selectedPage === this.pages.ORDER_MANAGEMENT.REFUND ||
        selectedPage === this.pages.ORDER_MANAGEMENT.NOTIFICATIONS ||
        selectedPage === this.pages.ORDER_MANAGEMENT.STATUS_MANAGEMENT ||
        selectedPage === this.pages.ORDER_MANAGEMENT.MAIL_INBOX ||
        selectedPage === this.pages.ORDER_MANAGEMENT.MAIL_SYNC_SETTING)
    ) {
      return "ORDER_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.PRODUCT_MANAGEMENT.PRODUCTS ||
        selectedPage === this.pages.PRODUCT_MANAGEMENT.OPTIONS)
    ) {
      return "PRODUCT_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.MATERIAL_MANAGEMENT.MATERIAL ||
        selectedPage === this.pages.MATERIAL_MANAGEMENT.INVENTORY)
    ) {
      return "MATERIAL_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.PRICING_MANAGEMENT.WASTE_RATE ||
        selectedPage === this.pages.PRICING_MANAGEMENT.SHIPPING_FEE ||
        selectedPage === this.pages.PRICING_MANAGEMENT.EVENT_DISCOUNT)
    ) {
      return "PRICING_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.TASK_MANAGEMENT.TASK_TEMPLATES ||
        selectedPage === this.pages.TASK_MANAGEMENT.STATUS ||
        selectedPage === this.pages.TASK_MANAGEMENT.PRODUCTION_RECORDS ||
        selectedPage === this.pages.TASK_MANAGEMENT.MAINTENANCE)
    ) {
      return "TASK_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      (selectedPage === this.pages.CONTENT_MANAGEMENT.NOTICES ||
        selectedPage === this.pages.CONTENT_MANAGEMENT.FAQS ||
        selectedPage === this.pages.CONTENT_MANAGEMENT.FONTS ||
        selectedPage === this.pages.CONTENT_MANAGEMENT.POPUPS ||
        selectedPage === this.pages.CONTENT_MANAGEMENT.PORTFOLIOS)
    ) {
      return "CONTENT_MANAGEMENT";
    } else if (
      selectedPage !== null &&
      selectedPage === this.pages.STATISTICS.KPI
    ) {
      return "STATISTICS";
    } else if (selectedPage !== null && selectedPage === this.pages.HOME) {
      return "HOME";
    } else {
      return null;
    }
  }

  getPageURL(target: SelectedPage): string {
    switch (target) {
      case this.pages.HOME:
        return `/company/${this.companyId}/`;

      // COMPANY MANAGEMENT
      case this.pages.COMPANY_MANAGEMENT.INFO:
        return `/company/${this.companyId}/info`;
      case this.pages.COMPANY_MANAGEMENT.MALL_DESIGN:
        return `/company/${this.companyId}/mall-design`;
      case this.pages.COMPANY_MANAGEMENT.ORGANIZATION:
        return `/company/${this.companyId}/organization`;
      case this.pages.COMPANY_MANAGEMENT.EQUIPMENTS:
        return `/company/${this.companyId}/equipments`;
      case this.pages.COMPANY_MANAGEMENT.EMAIL_SETTINGS:
        return `/company/${this.companyId}/email-settings`;
      case this.pages.COMPANY_MANAGEMENT.ROUTERS:
        return `/company/${this.companyId}/routers`;

      // CUSTOMER MANAGEMENT
      case this.pages.CUSTOMER_MANAGEMENT.MEMBERS:
        return `/company/${this.companyId}/members`;
      case this.pages.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS:
        return `/company/${this.companyId}/business-clients`;
      case this.pages.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES:
        return `/company/${this.companyId}/orders/quote-inquiries`;
      case this.pages.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS:
        return `/company/${this.companyId}/orders/order-revision-requests`;

      // PAYMENT
      case this.pages.PAYMENT_MANAGEMENT.BANK_TRANSFER:
        return `/company/${this.companyId}/payment/bank-transfer`;
      case this.pages.PAYMENT_MANAGEMENT.TOSS_PAYMENTS:
        return `/company/${this.companyId}/payment/toss-payments`;

      // ORDER
      case this.pages.ORDER_MANAGEMENT.ALL_ORDERS:
        return `/company/${this.companyId}/orders`;
      case this.pages.ORDER_MANAGEMENT.RESUBMISSION:
        return `/company/${this.companyId}/orders/resubmission`;
      case this.pages.ORDER_MANAGEMENT.SHIPPING:
        return `/company/${this.companyId}/orders/shipping`;
      case this.pages.ORDER_MANAGEMENT.TAX_RECEIPT:
        return `/company/${this.companyId}/orders/tax-receipt`;
      case this.pages.ORDER_MANAGEMENT.REFUND:
        return `/company/${this.companyId}/orders/refund`;
      case this.pages.ORDER_MANAGEMENT.NOTIFICATIONS:
        return `/company/${this.companyId}/orders/notifications`;
      case this.pages.ORDER_MANAGEMENT.STATUS_MANAGEMENT:
        return `/company/${this.companyId}/orders/status-management`;
      case this.pages.ORDER_MANAGEMENT.MAIL_INBOX:
        return `/company/${this.companyId}/orders/mail-inbox`;
      case this.pages.ORDER_MANAGEMENT.MAIL_SYNC_SETTING:
        return `/company/${this.companyId}/orders/mail-sync-setting`;

      // PRODUCT
      case this.pages.PRODUCT_MANAGEMENT.PRODUCTS:
        return `/company/${this.companyId}/products`;
      case this.pages.PRODUCT_MANAGEMENT.OPTIONS:
        return `/company/${this.companyId}/options`;

      // MATERIAL
      case this.pages.MATERIAL_MANAGEMENT.MATERIAL:
        return `/company/${this.companyId}/material`;
      case this.pages.MATERIAL_MANAGEMENT.INVENTORY:
        return `/company/${this.companyId}/inventory`;

      // TASK
      case this.pages.TASK_MANAGEMENT.TASK_TEMPLATES:
        return `/company/${this.companyId}/tasks/templates`;
      case this.pages.TASK_MANAGEMENT.STATUS:
        return `/company/${this.companyId}/tasks/status`;
      case this.pages.TASK_MANAGEMENT.PRODUCTION_RECORDS:
        return `/company/${this.companyId}/tasks/production-records`;
      case this.pages.TASK_MANAGEMENT.MAINTENANCE:
        return `/company/${this.companyId}/tasks/maintenance`;

      // CONTENT
      case this.pages.CONTENT_MANAGEMENT.NOTICES:
        return `/company/${this.companyId}/contents/notices`;
      case this.pages.CONTENT_MANAGEMENT.FAQS:
        return `/company/${this.companyId}/contents/faqs`;
      case this.pages.CONTENT_MANAGEMENT.FONTS:
        return `/company/${this.companyId}/contents/fonts`;
      case this.pages.CONTENT_MANAGEMENT.POPUPS:
        return `/company/${this.companyId}/contents/popups`;
      case this.pages.CONTENT_MANAGEMENT.PORTFOLIOS:
        return `/company/${this.companyId}/contents/portfolios`;

      // STATISTICS
      case this.pages.STATISTICS.KPI:
        return `/company/${this.companyId}/kpi`;

      // PRICING
      case this.pages.PRICING_MANAGEMENT.WASTE_RATE:
        return `/company/${this.companyId}/pricing/waste-rate`;
      case this.pages.PRICING_MANAGEMENT.SHIPPING_FEE:
        return `/company/${this.companyId}/pricing/shipping-fee`;
      case this.pages.PRICING_MANAGEMENT.EVENT_DISCOUNT:
        return `/company/${this.companyId}/pricing/event-discount`;

      default:
        return "/";
    }
  }

  checkLocation(): SelectedPage | null {
    if (!this.companyId) {
      return null;
    }

    switch (this.pathname) {
      case `/company/${this.companyId}/`:
        return this.pages.HOME;

      // COMPANY MANAGEMENT
      case `/company/${this.companyId}/info`:
        return this.pages.COMPANY_MANAGEMENT.INFO;
      case `/company/${this.companyId}/mall-design`:
        return this.pages.COMPANY_MANAGEMENT.MALL_DESIGN;
      case `/company/${this.companyId}/organization`:
      case `/company/${this.companyId}/staff`:
      case `/company/${this.companyId}/departments`:
        return this.pages.COMPANY_MANAGEMENT.ORGANIZATION;
      case `/company/${this.companyId}/equipments`:
        return this.pages.COMPANY_MANAGEMENT.EQUIPMENTS;
      case `/company/${this.companyId}/email-settings`:
        return this.pages.COMPANY_MANAGEMENT.EMAIL_SETTINGS;
      case `/company/${this.companyId}/routers`:
        return this.pages.COMPANY_MANAGEMENT.ROUTERS;

      // CUSTOMER MANAGEMENT
      case `/company/${this.companyId}/members`:
        return this.pages.CUSTOMER_MANAGEMENT.MEMBERS;
      case `/company/${this.companyId}/business-clients`:
        return this.pages.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS;
      case `/company/${this.companyId}/orders/quote-inquiries`:
        return this.pages.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES;
      case `/company/${this.companyId}/orders/order-revision-requests`:
        return this.pages.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS;

      // PAYMENT
      case `/company/${this.companyId}/payment/bank-transfer`:
        return this.pages.PAYMENT_MANAGEMENT.BANK_TRANSFER;
      case `/company/${this.companyId}/payment/toss-payments`:
        return this.pages.PAYMENT_MANAGEMENT.TOSS_PAYMENTS;

      // ORDER
      case `/company/${this.companyId}/orders`:
        return this.pages.ORDER_MANAGEMENT.ALL_ORDERS;
      case `/company/${this.companyId}/orders/resubmission`:
        return this.pages.ORDER_MANAGEMENT.RESUBMISSION;
      case `/company/${this.companyId}/orders/shipping`:
        return this.pages.ORDER_MANAGEMENT.SHIPPING;
      case `/company/${this.companyId}/orders/tax-receipt`:
        return this.pages.ORDER_MANAGEMENT.TAX_RECEIPT;
      case `/company/${this.companyId}/orders/refund`:
        return this.pages.ORDER_MANAGEMENT.REFUND;
      case `/company/${this.companyId}/orders/notifications`:
        return this.pages.ORDER_MANAGEMENT.NOTIFICATIONS;
      case `/company/${this.companyId}/orders/status-management`:
        return this.pages.ORDER_MANAGEMENT.STATUS_MANAGEMENT;
      case `/company/${this.companyId}/orders/mail-inbox`:
        return this.pages.ORDER_MANAGEMENT.MAIL_INBOX;
      case `/company/${this.companyId}/orders/mail-sync-setting`:
        return this.pages.ORDER_MANAGEMENT.MAIL_SYNC_SETTING;

      // PRODUCT
      case `/company/${this.companyId}/products`:
        return this.pages.PRODUCT_MANAGEMENT.PRODUCTS;
      case `/company/${this.companyId}/options`:
        return this.pages.PRODUCT_MANAGEMENT.OPTIONS;

      // MATERIAL
      case `/company/${this.companyId}/material`:
        return this.pages.MATERIAL_MANAGEMENT.MATERIAL;
      case `/company/${this.companyId}/inventory`:
        return this.pages.MATERIAL_MANAGEMENT.INVENTORY;

      // TASK
      case `/company/${this.companyId}/tasks/templates`:
        return this.pages.TASK_MANAGEMENT.TASK_TEMPLATES;
      case `/company/${this.companyId}/tasks/status`:
        return this.pages.TASK_MANAGEMENT.STATUS;
      case `/company/${this.companyId}/tasks/production-records`:
        return this.pages.TASK_MANAGEMENT.PRODUCTION_RECORDS;
      case `/company/${this.companyId}/tasks/maintenance`:
        return this.pages.TASK_MANAGEMENT.MAINTENANCE;

      // CONTENT
      case `/company/${this.companyId}/contents/notices`:
        return this.pages.CONTENT_MANAGEMENT.NOTICES;
      case `/company/${this.companyId}/contents/faqs`:
        return this.pages.CONTENT_MANAGEMENT.FAQS;
      case `/company/${this.companyId}/contents/fonts`:
        return this.pages.CONTENT_MANAGEMENT.FONTS;
      case `/company/${this.companyId}/contents/popups`:
        return this.pages.CONTENT_MANAGEMENT.POPUPS;
      case `/company/${this.companyId}/contents/portfolios`:
      case `/company/${this.companyId}/contents/portfolios/new`:
        return this.pages.CONTENT_MANAGEMENT.PORTFOLIOS;

      // STATISTICS
      case `/company/${this.companyId}/kpi`:
        return this.pages.STATISTICS.KPI;

      // PRICING
      case `/company/${this.companyId}/pricing/waste-rate`:
        return this.pages.PRICING_MANAGEMENT.WASTE_RATE;
      case `/company/${this.companyId}/pricing/shipping-fee`:
        return this.pages.PRICING_MANAGEMENT.SHIPPING_FEE;
      case `/company/${this.companyId}/pricing/event-discount`:
        return this.pages.PRICING_MANAGEMENT.EVENT_DISCOUNT;

      default:
        return null;
    }
  }
}

export const CompanyAdminLayout: React.FC<CompanyAdminLayoutProps> = ({
  children,
}) => {
  const location = useLocation();
  const { user } = useAuthStore();

  // 페이지 매니저
  const pageManager = useMemo(
    () => new PageManager(location.pathname, PAGES, user?.companyId),
    [user, location],
  );
  const selectedPage = pageManager.checkLocation();

  // 메뉴 자동 펼치기
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const isAlreadyAutoExpandedRef = useRef(false);
  useEffect(() => {
    if (isAlreadyAutoExpandedRef.current) return;
    isAlreadyAutoExpandedRef.current = true;

    const autoExpandingMenu = pageManager.getAutoExpandingMenu();

    setExpandedMenus((previous) => {
      if (autoExpandingMenu === null) return previous;
      return [...previous, autoExpandingMenu];
    });
  }, [expandedMenus, selectedPage, pageManager]);

  const handleLogoClick = () => {
    window.location.href = `/`;
  };

  const toggleMenu = (menuName: string) => {
    setExpandedMenus((prev) => {
      const isExpanded = prev.includes(menuName);

      return isExpanded
        ? prev.filter((item) => item !== menuName)
        : [...prev, menuName];
    });
  };

  // 현재 활성화된 컴포넌트 상태
  const [activeComponent, setActiveComponent] = useState<string | null>(null);

  const [company, setCompany] = useState<CompanyInfoResponse | null>(null);

  useEffect(() => {
    (async () => {
      if (user?.companyId) {
        const fetchedCompany = await companyUseCase.getCompanyInfo({
          companyId: user?.companyId,
        });
        setCompany(fetchedCompany);
      }
    })();
  }, [user?.companyId]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuSectionEnabled, setIsMobileMenuSectionEnabled] =
    useState(false);

  return (
    <>
      <div
        className={`${isMobileMenuOpen ? "opacity-[1]" : "opacity-0"} transition-all z-[500000] absolute top-0 bottom-0 left-0 right-0 md:hidden ${isMobileMenuSectionEnabled ? "block" : "hidden"}`}
      >
        <div
          className={`flex transition-all z-[100] relative h-screen bg-white shadow-sm border-r 
          border-gray-200  flex-col overflow-y-scroll overflow-x-hidden sidebar-no-scrollbar
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-[199px]"}`}
          style={{
            width: "200px",
            scrollbarWidth: "none" /* Firefox */,
            msOverflowStyle: "none" /* IE and Edge */,
          }}
        >
          {/* Logo */}
          <div className="pt-[26px] pl-[20px] pr-4 text-left w-full">
            <div className="mb-4 flex items-center justify-between w-full">
              <div className="w-full">
                <div className="flex justify-between w-full">
                  <div
                    className="font-medium text-lg text-[#252525]"
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {user?.name}
                  </div>
                  <div
                    onClick={async () => {
                      setIsMobileMenuOpen(false);
                      await new Promise((resolve) => {
                        setTimeout(resolve, 500);
                      });
                      setIsMobileMenuSectionEnabled(false);
                    }}
                  >
                    <LucidePlus className="rotate-45 scale-[1.2]" />
                  </div>
                </div>

                <div
                  className="text-xs text-[#555555] mt-1 font-normal"
                  style={{ fontFamily: "Pretendard" }}
                >
                  {user?.email}
                </div>
              </div>
            </div>
            {company?.phone ||
            company?.address ||
            company?.addressDetail ||
            company?.businessNumber ? (
              <div className="flex flex-col">
                <div className="text-sm text-[#555555] mb-2">
                  {company?.phone}
                </div>
                <div className="text-sm text-[#555555]">{company?.address}</div>
                <div className="text-sm text-[#555555] mb-2">
                  {company?.addressDetail}
                </div>
                <div className="text-sm text-[#555555]">
                  {company?.businessNumber}
                </div>
              </div>
            ) : null}
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 pt-8 min-w-[199px]">
            {/* Main Menu Label */}
            <div
              className="text-xs font-semibold text-gray-400 ml-[20px] mb-[0.875rem] text-left"
              style={{ fontFamily: "Pretendard" }}
            >
              Main Menu
            </div>

            {/* 관리자 홈 */}

            <div className="mb-4">
              <a
                href={pageManager.getPageURL(PAGES.HOME)}
                className={`flex items-center py-[0.7188rem] text-sm font-normal ${
                  selectedPage !== null && selectedPage === PAGES.HOME
                    ? "text-[#4285F4] bg-[#F5F8FF]"
                    : "text-[#555555]"
                } rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPage !== null && selectedPage === PAGES.HOME ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]" />
                ) : null}
                <img
                  className="ml-[22px] mr-[0.5rem] w-5"
                  src="/assets/icons/Home.svg"
                  alt="쇼핑 가방 아이콘"
                />
                관리자 홈
              </a>
            </div>

            {/* Sub Menu Label */}
            <div
              className="text-xs font-semibold text-gray-400 ml-[20px] mb-[0.875rem] text-left mt-[18px]"
              style={{ fontFamily: "Pretendard" }}
            >
              Sub Menu
            </div>

            {/* 쇼핑몰 관리 */}
            <div className="mb-[0.7188rem]">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.COMPANY_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["INFO", "MALL_DESIGN", "ORGANIZATION", "EQUIPMENTS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPage !== null &&
                ["INFO", "MALL_DESIGN", "ORGANIZATION", "EQUIPMENTS"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Work.svg"
                    alt="쇼핑 가방 아이콘"
                  />
                  내 회사 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("COMPANY_MANAGEMENT") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedMenus.includes("COMPANY_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.INFO)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.INFO ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 내 회사 정보
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.MALL_DESIGN)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.MALL_DESIGN ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 쇼핑몰 디자인
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.ORGANIZATION)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.ORGANIZATION ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 조직 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.EQUIPMENTS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.EQUIPMENTS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 장비 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.EMAIL_SETTINGS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === "EMAIL_SETTINGS" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 이메일 설정
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.ROUTERS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.ROUTERS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 라우터 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 고객 관리 */}
            <div className="mb-[0.7188rem]">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.CUSTOMER_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["MEMBERS", "BUSINESS_CLIENTS", "QUOTE_INQUIRIES", "ORDER_REVISION_REQUESTS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                style={{ fontFamily: "Pretendard" }}
              >
                {selectedPage !== null &&
                ["MEMBERS", "BUSINESS_CLIENTS", "QUOTE_INQUIRIES", "ORDER_REVISION_REQUESTS"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Profile.svg"
                    alt="고객 관리 아이콘"
                  />
                  고객 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("CUSTOMER_MANAGEMENT") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedMenus.includes("CUSTOMER_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.MEMBERS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.MEMBERS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 회원 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 기업 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 견적 문의
                    </a>
                    <a
                      href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS)}
                      className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                    >
                      • 주문별 문의 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 결제 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.PAYMENT_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["BANK_TRANSFER", "TOSS_PAYMENTS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["BANK_TRANSFER", "TOSS_PAYMENTS"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Wallet.svg"
                    alt="쇼핑 가방 아이콘"
                  />
                  결제 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("PAYMENT_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("PAYMENT_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.PAYMENT_MANAGEMENT.BANK_TRANSFER,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "BANK_TRANSFER"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 무통장 입금
                    </a>
                    <a
                      href={`/company/${user?.companyId}/payment/toss-payments`}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage === "TOSS_PAYMENTS"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 토스 페이
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 주문 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.ORDER_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["ALL_ORDERS", "RESUBMISSION", "SHIPPING", "TAX_RECEIPT", "REFUND", "NOTIFICATIONS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["ALL_ORDERS", "RESUBMISSION", "SHIPPING", "TAX_RECEIPT", "REFUND", "NOTIFICATIONS"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Buy.svg"
                    alt="쇼핑 가방 아이콘"
                  />
                  주문 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("ORDER_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("ORDER_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.ALL_ORDERS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.ALL_ORDERS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 전체 주문 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.RESUBMISSION,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.RESUBMISSION
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 재접수 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.SHIPPING,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.SHIPPING
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 배송 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.TAX_RECEIPT,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.TAX_RECEIPT
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 세금계산서/현금영수증
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.REFUND,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.REFUND
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 반품 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.NOTIFICATIONS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "NOTIFICATIONS"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 이메일, 문자, SNS 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.STATUS_MANAGEMENT,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "STATUS_MANAGEMENT"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 현황 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.ORDER_MANAGEMENT.MAIL_INBOX,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.ORDER_MANAGEMENT.MAIL_INBOX
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 주문 메일함
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 상품 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.PRODUCT_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["PRODUCTS", "OPTIONS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["PRODUCTS", "OPTIONS"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Category.svg"
                    alt="쇼핑 가방 아이콘"
                  />
                  상품 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("PRODUCT_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("PRODUCT_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.PRODUCT_MANAGEMENT.PRODUCTS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.PRODUCT_MANAGEMENT.PRODUCTS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 상품 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 자재관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.MATERIAL_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["MATERIAL", "INVENTORY"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["MATERIAL", "INVENTORY"].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Table.svg"
                    alt="자재관리 아이콘"
                  />
                  자재관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("MATERIAL_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("MATERIAL_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.MATERIAL_MANAGEMENT.MATERIAL,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.MATERIAL_MANAGEMENT.MATERIAL
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 자재품목관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.MATERIAL_MANAGEMENT.INVENTORY,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.MATERIAL_MANAGEMENT.INVENTORY
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 재고관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 가격 정보 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.PRICING_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["WASTE_RATE", "SHIPPING_FEE", "EVENT_DISCOUNT"].includes(
                        selectedPage,
                      )
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["WASTE_RATE", "SHIPPING_FEE", "EVENT_DISCOUNT"].includes(
                  selectedPage,
                ) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <svg
                    className="ml-[22px] mr-[0.5rem] w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  가격 정보 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("PRICING_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("PRICING_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.PRICING_MANAGEMENT.WASTE_RATE,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.PRICING_MANAGEMENT.WASTE_RATE
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 손지율 비용 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.PRICING_MANAGEMENT.SHIPPING_FEE,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.PRICING_MANAGEMENT.SHIPPING_FEE
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 배송비 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.PRICING_MANAGEMENT.EVENT_DISCOUNT,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "EVENT_DISCOUNT"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 이벤트 할인 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 작업 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.TASK_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      [
                        "TASK_TEMPLATES",
                        "STATUS",
                        "PRODUCTION_RECORDS",
                        "MAINTENANCE",
                      ].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                [
                  "TASK_TEMPLATES",
                  "STATUS",
                  "PRODUCTION_RECORDS",
                  "MAINTENANCE",
                ].includes(selectedPage) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <svg
                    className="ml-[22px] mr-[0.5rem] w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                    />
                  </svg>
                  작업 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("TASK_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("TASK_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.TASK_MANAGEMENT.TASK_TEMPLATES,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "TASK_TEMPLATES"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 작업 공정 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.TASK_MANAGEMENT.STATUS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.TASK_MANAGEMENT.STATUS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 작업 현황 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.TASK_MANAGEMENT.PRODUCTION_RECORDS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === "PRODUCTION_RECORDS"
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 생산 실적 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.TASK_MANAGEMENT.MAINTENANCE,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.TASK_MANAGEMENT.MAINTENANCE
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 설비 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 컨텐츠 관리 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.CONTENT_MANAGEMENT)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["NOTICES", "FAQS", "FONTS", "POPUPS"].includes(
                        selectedPage,
                      )
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                ["NOTICES", "FAQS", "FONTS", "POPUPS"].includes(
                  selectedPage,
                ) ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/Edit.svg"
                    alt="컨텐츠 아이콘"
                  />
                  컨텐츠 관리
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("CONTENT_MANAGEMENT")
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("CONTENT_MANAGEMENT") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(
                        PAGES.CONTENT_MANAGEMENT.NOTICES,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.CONTENT_MANAGEMENT.NOTICES
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 공지사항 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.CONTENT_MANAGEMENT.FAQS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.CONTENT_MANAGEMENT.FAQS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • FAQ 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.CONTENT_MANAGEMENT.FONTS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.CONTENT_MANAGEMENT.FONTS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 폰트 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.CONTENT_MANAGEMENT.POPUPS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.CONTENT_MANAGEMENT.POPUPS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 팝업 관리
                    </a>
                    <a
                      href={pageManager.getPageURL(
                        PAGES.CONTENT_MANAGEMENT.PORTFOLIOS,
                      )}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.CONTENT_MANAGEMENT.PORTFOLIOS
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • 포트폴리오 관리
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* 통계 */}
            <div className="mb-2">
              <button
                onClick={() => toggleMenu(PAGE_CATEGORIES.STATISTICS)}
                className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      selectedPage === PAGES.STATISTICS.KPI
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
              >
                {selectedPage !== null &&
                selectedPage === PAGES.STATISTICS.KPI ? (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                ) : null}
                <div className="flex items-center">
                  <img
                    className="ml-[22px] mr-[0.5rem] w-5"
                    src="/assets/icons/KPI.svg"
                    alt="통계 아이콘"
                  />
                  통계
                </div>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                    expandedMenus.includes("STATISTICS") ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedMenus.includes("STATISTICS") && (
                <div>
                  <div className="ml-10 mt-1 space-y-1 text-left">
                    <a
                      href={pageManager.getPageURL(PAGES.STATISTICS.KPI)}
                      className={`block py-[10px] text-[13px] ${
                        selectedPage !== null &&
                        selectedPage === PAGES.STATISTICS.KPI
                          ? "text-[#4285F4]"
                          : "text-[#757575]"
                      }  hover:text-gray-900 text-left font-normal`}
                    >
                      • KPI
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-40"></div>
          </nav>
        </div>

        <div className="absolute bg-black opacity-[0.4] top-0 bottom-0 left-0 right-0"></div>
      </div>

      <style>
        {`
          .sidebar-no-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <div
        className="flex flex-col relative h-screen
        / bg-gray-50 overflow-hidden"
      >
        <div className="md:hidden z-[10000000] absolute left-0 bottom-16 flex justify-start items-center"></div>
        <AdminHeader
          onLogoClick={handleLogoClick}
          showMyPageLink={true}
          mobileMenuToggleHandler={async () => {
            setIsMobileMenuSectionEnabled(true);
            await new Promise((resolve) => {
              setTimeout(resolve, 200);
            });
            setIsMobileMenuOpen(true);
          }}
        />

        {/* Body and Footer */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className="absolute md:relative">
            {/*
              Sidebar. 높이는 본문 영역(화면 - 상단 헤더)에 맞춘다. h-screen 이면 헤더 높이만큼 넘쳐서
              바깥 overflow-hidden 영역이 포커스 · scrollIntoView 때 그만큼 밀려 올라갔다 (2026-10-26).
            */}

            <div
              className="hidden md:flex transition-all z-[100] relative h-full bg-white shadow-sm border-r border-gray-200  flex-col overflow-y-scroll overflow-x-hidden sidebar-no-scrollbar"
              style={{
                width: "200px",
                scrollbarWidth: "none" /* Firefox */,
                msOverflowStyle: "none" /* IE and Edge */,
              }}
            >
              {/* Logo */}
              <div className="pt-[26px] pl-[20px] pr-4 text-left">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <div
                      className="font-medium text-lg text-[#252525]"
                      style={{ fontFamily: "Pretendard" }}
                    >
                      {user?.name}
                    </div>
                    <div
                      className="text-xs text-[#555555] mt-1 font-normal"
                      style={{ fontFamily: "Pretendard" }}
                    >
                      {user?.email}
                    </div>
                  </div>
                </div>
                {company?.phone ||
                company?.address ||
                company?.addressDetail ||
                company?.businessNumber ? (
                  <div className="flex flex-col">
                    <div className="text-sm text-[#555555] mb-2">
                      {company?.phone}
                    </div>
                    <div className="text-sm text-[#555555]">
                      {company?.address}
                    </div>
                    <div className="text-sm text-[#555555] mb-2">
                      {company?.addressDetail}
                    </div>
                    <div className="text-sm text-[#555555]">
                      {company?.businessNumber}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Navigation Menu */}
              <nav className="flex-1 pt-8 min-w-[199px]">
                {/* Main Menu Label */}
                <div
                  className="text-xs font-semibold text-gray-400 ml-[20px] mb-[0.875rem] text-left"
                  style={{ fontFamily: "Pretendard" }}
                >
                  Main Menu
                </div>

                {/* 관리자 홈 */}

                <div className="mb-4">
                  <a
                    href={pageManager.getPageURL(PAGES.HOME)}
                    className={`flex items-center py-[0.7188rem] text-sm font-normal ${
                      selectedPage !== null && selectedPage === PAGES.HOME
                        ? "text-[#4285F4] bg-[#F5F8FF]"
                        : "text-[#555555]"
                    } rounded relative`}
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {selectedPage !== null && selectedPage === PAGES.HOME ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]" />
                    ) : null}
                    <img
                      className="ml-[22px] mr-[0.5rem] w-5"
                      src="/assets/icons/Home.svg"
                      alt="쇼핑 가방 아이콘"
                    />
                    관리자 홈
                  </a>
                </div>

                {/* Sub Menu Label */}
                <div
                  className="text-xs font-semibold text-gray-400 ml-[20px] mb-[0.875rem] text-left mt-[18px]"
                  style={{ fontFamily: "Pretendard" }}
                >
                  Sub Menu
                </div>

                {/* 쇼핑몰 관리 */}
                <div className="mb-[0.7188rem]">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.COMPANY_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      [
                        "INFO",
                        "MEMBERS",
                        "MALL_DESIGN",
                        "ORGANIZATION",
                        "BUSINESS_CLIENTS",
                        "EQUIPMENTS",
                      ].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                    style={{ fontFamily: "Pretendard" }}
                  >
                    {selectedPage !== null &&
                    [
                      "INFO",
                      "MEMBERS",
                      "MALL_DESIGN",
                      "ORGANIZATION",
                      "BUSINESS_CLIENTS",
                      "EQUIPMENTS",
                    ].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Work.svg"
                        alt="쇼핑 가방 아이콘"
                      />
                      내 회사 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("COMPANY_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("COMPANY_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.INFO)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.INFO ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 내 회사 정보
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.MALL_DESIGN)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "MALL_DESIGN" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 쇼핑몰 디자인
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.ORGANIZATION)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.ORGANIZATION ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 조직 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.EQUIPMENTS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "EQUIPMENTS" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 장비 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.ROUTERS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.COMPANY_MANAGEMENT.ROUTERS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 라우터 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.COMPANY_MANAGEMENT.EMAIL_SETTINGS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "EMAIL_SETTINGS" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 이메일 설정
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 고객 관리 */}
                <div className="mb-[0.7188rem]">
                  <button
                    onClick={() => toggleMenu(PAGE_CATEGORIES.CUSTOMER_MANAGEMENT)}
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["MEMBERS", "BUSINESS_CLIENTS", "QUOTE_INQUIRIES", "ORDER_REVISION_REQUESTS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["MEMBERS", "BUSINESS_CLIENTS", "QUOTE_INQUIRIES", "ORDER_REVISION_REQUESTS"].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Profile.svg"
                        alt="고객 관리 아이콘"
                      />
                      고객 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${expandedMenus.includes("CUSTOMER_MANAGEMENT") ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedMenus.includes("CUSTOMER_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.MEMBERS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.MEMBERS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 회원 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.BUSINESS_CLIENTS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 기업 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.QUOTE_INQUIRIES ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 견적 문의
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.CUSTOMER_MANAGEMENT.ORDER_REVISION_REQUESTS ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 주문별 문의 관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 결제 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.PAYMENT_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["BANK_TRANSFER", "TOSS_PAYMENTS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["BANK_TRANSFER", "TOSS_PAYMENTS"].includes(
                      selectedPage,
                    ) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Wallet.svg"
                        alt="쇼핑 가방 아이콘"
                      />
                      결제 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("PAYMENT_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("PAYMENT_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.PAYMENT_MANAGEMENT.BANK_TRANSFER,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "BANK_TRANSFER"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 무통장 입금
                        </a>
                        <a
                          href={`/company/${user?.companyId}/payment/toss-payments`}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage === "TOSS_PAYMENTS"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 토스 페이
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 주문 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() => toggleMenu(PAGE_CATEGORIES.ORDER_MANAGEMENT)}
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["ALL_ORDERS", "RESUBMISSION", "SHIPPING", "TAX_RECEIPT", "REFUND", "NOTIFICATIONS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["ALL_ORDERS", "RESUBMISSION", "SHIPPING", "TAX_RECEIPT", "REFUND", "NOTIFICATIONS"].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Buy.svg"
                        alt="쇼핑 가방 아이콘"
                      />
                      주문 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${expandedMenus.includes("ORDER_MANAGEMENT") ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedMenus.includes("ORDER_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.ALL_ORDERS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "ALL_ORDERS" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 전체 주문 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.RESUBMISSION)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "RESUBMISSION" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 재접수 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.SHIPPING)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.ORDER_MANAGEMENT.SHIPPING ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 배송 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.TAX_RECEIPT)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "TAX_RECEIPT" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 세금계산서/현금영수증
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.REFUND)}
                          className={`block py-[10px] text-[13px] ${selectedPage === PAGES.ORDER_MANAGEMENT.REFUND ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 반품 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.NOTIFICATIONS)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "NOTIFICATIONS" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 이메일, 문자, SNS 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(PAGES.ORDER_MANAGEMENT.STATUS_MANAGEMENT)}
                          className={`block py-[10px] text-[13px] ${selectedPage === "STATUS_MANAGEMENT" ? "text-[#4285F4]" : "text-[#757575]"} hover:text-gray-900 text-left font-normal`}
                        >
                          • 현황 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.ORDER_MANAGEMENT.MAIL_INBOX,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.ORDER_MANAGEMENT.MAIL_INBOX
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 주문 메일함
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 상품 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.PRODUCT_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["PRODUCTS", "OPTIONS"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["PRODUCTS", "OPTIONS"].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Category.svg"
                        alt="쇼핑 가방 아이콘"
                      />
                      상품 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("PRODUCT_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("PRODUCT_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.PRODUCT_MANAGEMENT.PRODUCTS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.PRODUCT_MANAGEMENT.PRODUCTS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 상품 관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 자재관리 */}
                <div className="mb-2">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.MATERIAL_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["MATERIAL", "INVENTORY"].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["MATERIAL", "INVENTORY"].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Table.svg"
                        alt="자재관리 아이콘"
                      />
                      자재관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("MATERIAL_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("MATERIAL_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.MATERIAL_MANAGEMENT.MATERIAL,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.MATERIAL_MANAGEMENT.MATERIAL
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 자재품목관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.MATERIAL_MANAGEMENT.INVENTORY,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "INVENTORY"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 재고관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 가격 정보 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.PRICING_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      ["WASTE_RATE", "SHIPPING_FEE", "EVENT_DISCOUNT"].includes(
                        selectedPage,
                      )
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["WASTE_RATE", "SHIPPING_FEE", "EVENT_DISCOUNT"].includes(
                      selectedPage,
                    ) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <svg
                        className="ml-[22px] mr-[0.5rem] w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      가격 정보 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("PRICING_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("PRICING_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.PRICING_MANAGEMENT.WASTE_RATE,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "WASTE_RATE"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 손지율 비용 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.PRICING_MANAGEMENT.SHIPPING_FEE,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "SHIPPING_FEE"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 배송비 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.PRICING_MANAGEMENT.EVENT_DISCOUNT,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "EVENT_DISCOUNT"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 이벤트 할인 관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 작업 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() => toggleMenu(PAGE_CATEGORIES.TASK_MANAGEMENT)}
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      [
                        "TASK_TEMPLATES",
                        "STATUS",
                        "PRODUCTION_RECORDS",
                        "MAINTENANCE",
                      ].includes(selectedPage)
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    [
                      "TASK_TEMPLATES",
                      "STATUS",
                      "PRODUCTION_RECORDS",
                      "MAINTENANCE",
                    ].includes(selectedPage) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <svg
                        className="ml-[22px] mr-[0.5rem] w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                        />
                      </svg>
                      작업 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("TASK_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("TASK_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.TASK_MANAGEMENT.TASK_TEMPLATES,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "TASK_TEMPLATES"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 작업 공정 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.TASK_MANAGEMENT.STATUS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.TASK_MANAGEMENT.STATUS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 작업 현황 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.TASK_MANAGEMENT.PRODUCTION_RECORDS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "PRODUCTION_RECORDS"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 생산 실적 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.TASK_MANAGEMENT.MAINTENANCE,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === "MAINTENANCE"
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 설비 관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 컨텐츠 관리 */}
                <div className="mb-2">
                  <button
                    onClick={() =>
                      toggleMenu(PAGE_CATEGORIES.CONTENT_MANAGEMENT)
                    }
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      selectedPage === PAGES.CONTENT_MANAGEMENT.FONTS
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    } rounded relative`}
                  >
                    {selectedPage !== null &&
                    ["NOTICES", "FAQS", "FONTS", "POPUPS"].includes(
                      selectedPage,
                    ) ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/Edit.svg"
                        alt="컨텐츠 아이콘"
                      />
                      컨텐츠 관리
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("CONTENT_MANAGEMENT")
                          ? "rotate-180"
                          : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("CONTENT_MANAGEMENT") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(
                            PAGES.CONTENT_MANAGEMENT.NOTICES,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.CONTENT_MANAGEMENT.NOTICES
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 공지사항 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.CONTENT_MANAGEMENT.FAQS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.CONTENT_MANAGEMENT.FAQS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • FAQ 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.CONTENT_MANAGEMENT.FONTS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.CONTENT_MANAGEMENT.FONTS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 폰트 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.CONTENT_MANAGEMENT.POPUPS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.CONTENT_MANAGEMENT.POPUPS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 팝업 관리
                        </a>
                        <a
                          href={pageManager.getPageURL(
                            PAGES.CONTENT_MANAGEMENT.PORTFOLIOS,
                          )}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.CONTENT_MANAGEMENT.PORTFOLIOS
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • 포트폴리오 관리
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* 통계 */}
                <div className="mb-2">
                  <button
                    onClick={() => toggleMenu(PAGE_CATEGORIES.STATISTICS)}
                    className={`flex items-center justify-between w-full py-[0.7188rem] text-sm font-normal
                    ${
                      selectedPage !== null &&
                      selectedPage === PAGES.STATISTICS.KPI
                        ? "bg-[#F5F8FF] text-[#4285F4]"
                        : "text-[#555555] hover:bg-gray-50"
                    }  rounded relative`}
                  >
                    {selectedPage !== null &&
                    selectedPage === PAGES.STATISTICS.KPI ? (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#4285F4]"></div>
                    ) : null}
                    <div className="flex items-center">
                      <img
                        className="ml-[22px] mr-[0.5rem] w-5"
                        src="/assets/icons/KPI.svg"
                        alt="통계 아이콘"
                      />
                      통계
                    </div>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform mr-3 ${
                        expandedMenus.includes("STATISTICS") ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedMenus.includes("STATISTICS") && (
                    <div>
                      <div className="ml-10 mt-1 space-y-1 text-left">
                        <a
                          href={pageManager.getPageURL(PAGES.STATISTICS.KPI)}
                          className={`block py-[10px] text-[13px] ${
                            selectedPage !== null &&
                            selectedPage === PAGES.STATISTICS.KPI
                              ? "text-[#4285F4]"
                              : "text-[#757575]"
                          }  hover:text-gray-900 text-left font-normal`}
                        >
                          • KPI
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-40"></div>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto overflow-x-scroll bg-gray-100">
            {/* Content Area */}
            <main className="h-full">
              {activeComponent === "order-management" && <OrderManagement />}
              {activeComponent === "resubmission-management" && (
                <ResubmissionManagement />
              )}
              {activeComponent === "shipping-management" && (
                <ShippingManagement />
              )}
              {!activeComponent && children}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};
